import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { SavingAgreementNotes } from "@app/models/saving-agreement-notes";
import { SavingAgreementNotesService } from "@app/services/saving-agreement-notes.service";

@Component({
    selector: "app-select-saving-agreement-note-popup",
    templateUrl: "./select-saving-agreement-note-popup.component.html",
    styleUrls: ["./select-saving-agreement-note-popup.component.css"],
    standalone: false
})
export class SelectSavingAgreementNotePopupComponent implements OnInit {
  @Input() savingAgreementNotes: Array<SavingAgreementNotes>;
  @Input() displaySearchDialog: boolean;
  @Input() selectedSavingAgreementNotes: Array<SavingAgreementNotes>;
  @Output() onSubmit = new EventEmitter<Array<SavingAgreementNotes>>();
  @Output() onCancel = new EventEmitter();

  constructor(private savingAgreementNotesService: SavingAgreementNotesService) {}

  ngOnInit() {
    if (!this.selectedSavingAgreementNotes) {
      this.selectedSavingAgreementNotes = [];
    } else {
      this.selectedSavingAgreementNotes = [...this.selectedSavingAgreementNotes];
    }
    this.addMandatoryNotes();
    this.syncInitialSelection();
  }

  private addMandatoryNotes() {
    for (const root of this.savingAgreementNotes || []) for (const note of this.findAllChidren(root)) if (note.mandatory && note.status !== 'INACTIVE') for (const child of this.findAllChidren(note)) if (child.status !== 'INACTIVE' && !this.selectedSavingAgreementNotes.some((selected) => selected.id === child.id)) this.selectedSavingAgreementNotes.push(child);
  }

  syncInitialSelection() {
    if (!this.savingAgreementNotes || this.savingAgreementNotes.length === 0) {
      return;
    }

    const currentList = [...this.selectedSavingAgreementNotes];
    for (const note of currentList) {
      const fullNote = this.savingAgreementNotesService.findSavingAgreementNoteById(note.id, this.savingAgreementNotes) || note;
      const allChildren = this.findAllChidren(fullNote);
      for (const child of allChildren) {
        if (!this.selectedSavingAgreementNotes.some((n) => n.id === child.id)) {
          this.selectedSavingAgreementNotes.push(child);
        }
      }
    }

    this.updateAllParentsSelection();
  }

  hideSearch() {
    this.onCancel.emit();
  }

  onSelect(savingAgreementNote: SavingAgreementNotes) {
    if (!savingAgreementNote) {
      return;
    }

    const fullNote = this.savingAgreementNotesService.findSavingAgreementNoteById(savingAgreementNote.id, this.savingAgreementNotes) || savingAgreementNote;
    const allDescendants = this.findAllChidren(fullNote);
    const updated = [...this.selectedSavingAgreementNotes];
    for (const item of allDescendants) {
      if (!updated.some((p) => p.id === item.id)) {
        updated.push(item);
      }
    }
    this.selectedSavingAgreementNotes = updated;

    this.updateAllParentsSelection();
  }

  onSave() {
    this.onSubmit.emit(this.selectedSavingAgreementNotes);
  }

  deleteSelectedSavingAgreementNote(savingAgreementNote: SavingAgreementNotes) {
    if (!savingAgreementNote) {
      return;
    }

    const fullNote = this.savingAgreementNotesService.findSavingAgreementNoteById(savingAgreementNote.id, this.savingAgreementNotes) || savingAgreementNote;
    const allDescendants = this.findAllChidren(fullNote);
    const removeIds = new Set(allDescendants.map((item) => item.id));

    this.selectedSavingAgreementNotes = this.selectedSavingAgreementNotes.filter((item) => !removeIds.has(item.id));
  }

  updateAllParentsSelection() {
    if (!this.savingAgreementNotes || this.savingAgreementNotes.length === 0) {
      return;
    }

    const selectedSet = new Set(this.selectedSavingAgreementNotes.map((n) => n.id));

    const checkNode = (node: SavingAgreementNotes): boolean => {
      if (!node.subSavingAgreementNotes || node.subSavingAgreementNotes.length === 0) {
        return selectedSet.has(node.id);
      }

      let allChildrenSelected = true;
      for (const child of node.subSavingAgreementNotes) {
        const childSelected = checkNode(child);
        if (!childSelected) {
          allChildrenSelected = false;
        }
      }

      if (allChildrenSelected) {
        selectedSet.add(node.id);
      } else {
        selectedSet.delete(node.id);
      }

      return allChildrenSelected;
    };

    for (const rootNode of this.savingAgreementNotes) {
      checkNode(rootNode);
    }

    const newSelected: SavingAgreementNotes[] = [];
    for (const item of this.selectedSavingAgreementNotes) {
      if (selectedSet.has(item.id) && !newSelected.some((n) => n.id === item.id)) {
        newSelected.push(item);
      }
    }
    for (const id of selectedSet) {
      if (!newSelected.some((n) => n.id === id)) {
        const found = this.savingAgreementNotesService.findSavingAgreementNoteById(id, this.savingAgreementNotes);
        if (found) {
          newSelected.push(found);
        }
      }
    }

    this.selectedSavingAgreementNotes = newSelected;
  }

  findAllChidren(savingAgreementNote: SavingAgreementNotes) {
    let items: SavingAgreementNotes[] = [];
    if (savingAgreementNote) {
      items.push(savingAgreementNote);
      if (savingAgreementNote.subSavingAgreementNotes && savingAgreementNote.subSavingAgreementNotes.length > 0) {
          savingAgreementNote.subSavingAgreementNotes.forEach((p) => {
          items = items.concat(this.findAllChidren(p));
        });
      }
    }
    return items;
  }
}
